import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store';
import FilterSelect from '@/components/filters/FilterSelect';
import Search from '@/components/filters/Search';
import { AdminCampaignTypes, Status } from './types';
import AnalyticsCard from '@/components/cards/AnalyticsCard';
import { fetchCategories } from '@/store/Category';
import { fetchAllCampaignsAdmin, fetchCampaignStats } from '@/store/Campaign';
import CampaignTable from './components/CampaignTable';

const statusList: (Status | 'All')[] = [
  'All',
  'Approved',
  'Rejected',
  'Pending',
  'Active',
  'Paused',
  'Completed',
];

type AdminStats = {
total: number,
approved: number,
pending: number,
rejected: number,
active: number,
}

const AdminCampaign: React.FC = () => {
  const [category, setCategory] = useState('All');
  const [categories, setCategories] = useState<string[]>(['All']);
  const [status, setStatus] = useState<Status | 'All'>('All');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [adminStats, setAdminStats] = useState<AdminStats>();

  const dispatch = useDispatch<AppDispatch>();
  const dispatchForC = useDispatch<any>();
  const {
    adminList = [],
    adminPagination,
    status: sliceStatus,
  } = useSelector((s: RootState) => s.campaign);

  const totalPages = adminPagination?.totalPages ?? 1;
  const rows = useMemo(
    () => (Array.isArray(adminList) ? adminList : []),
    [adminList],
  );

  const stats = useMemo(
    () => ({
      total: Number(adminStats?.total ?? 0),
      approved: Number(adminStats?.approved ?? 0),
      pending: Number(adminStats?.pending ?? 0),
      rejected: Number(adminStats?.rejected ?? 0),
      active: Number(adminStats?.active ?? 0),
    }),
    [adminStats],
  );

  const cards = [
    {
      label: 'Total Requested',
      value: stats.total,
    },
    {
      label: 'Currently Live',
      value: stats.active,
    },
    {
      label: 'Approved',
      value: stats.approved,
    },
    {
      label: 'Rejected',
      value: stats.rejected,
    },
    {
      label: 'Pending Approval',
      value: stats.pending,
    },
  ];

  useEffect(() => {
    const loadData = async () => {
      try {
        const resStats = await dispatchForC(fetchCampaignStats());
        setAdminStats(resStats.payload.data[0]);
        
        const res = await dispatchForC(fetchCategories());
        const data = res.payload?.data || [];
        const names = ['All', ...data.map((item: any) => item.name)];
        setCategories(names);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    loadData();
  }, []);

  /* ─── first load & filters change ───────────────────────────────────────── */
  useEffect(() => {
    setPage(1); // Reset to page 1 on filter change
    dispatch(
      fetchAllCampaignsAdmin({
        title: search || undefined,
        category: category !== 'All' ? category : undefined,
        status: status !== 'All' ? status.toLowerCase() : undefined,
        page: 1,
        limit: 20,
      }),
    );
  }, [dispatch, search, category, status]);

  /* ─── infinite-scroll callback ──────────────────────────────────────────── */
  const loadMore = () => {
    if (page >= totalPages || sliceStatus === 'loading') return;
    const next = page + 1;
    setPage(next);
    dispatch(
      fetchAllCampaignsAdmin({
        title: search || undefined,
        category: category !== 'All' ? category : undefined,
        status: status !== 'All' ? status.toLowerCase() : undefined,
        page: next,
        limit: 20,
      }),
    );
  };

  /* ─── map API → table model ─────────────────────────────────────────────── */
  const formatData = (dataArr: any[]): AdminCampaignTypes[] =>
    dataArr.map((c) => ({
      id: c.id,
      title: c.title,
      gamic_id: c.gamic_id,
      audience_id: c.audience_id,
      user_id: c.user_id,
      start_date: c.start_date,
      end_date: c.end_date,
      daily_budget: c.daily_budget,
      total_budget: c.total_budget,
      status: c.status,
      is_deleted: c.is_deleted,
      by_admin: c.by_admin,
      cpe: c.cpe,
      cpd: c.cpd,
      created_at: c.created_at,
      gamic_title: c.gamic_title,
      image_url: c.image_url,
      spent: c.spent,
      impressions: c.impressions,
    }));

  return (
    <div className='flex h-full flex-col'>
      <div className='grid mb-4 grid-cols-5 gap-4'>
        {cards.map((c) => (
          <AnalyticsCard key={c.label} label={c.label} value={c.value} />
        ))}
      </div>
      <div className='p-8 rounded-[24px] bg-white flex flex-col h-full min-h-0 space-y-[24px]'>
        {/* ─── header & filters ───────────────────────────────────────────── */}
        <div className='flex items-center gap-2 justify-between'>
          <div className='text-[26px] font-[600]'>
            Showing {rows.length} Results
          </div>

          <div className='flex-wrap inline-flex items-center gap-2 lg:gap-0 space-x-[10px]'>
            <Search
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <FilterSelect
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              options={[
                { value: 'All', label: 'Category: All' },
                ...categories
                  .filter((c) => c !== 'All')
                  .map((c) => ({ value: c, label: `Category: ${c}` })),
              ]}
            />
            <FilterSelect
              value={status}
              onChange={(e) => setStatus(e.target.value as Status | 'All')}
              options={statusList.map((s) => ({
                value: s,
                label: `Status: ${s}`,
              }))}
            />
          </div>
        </div>

        {/* ─── table ──────────────────────────────────────────────────────── */}
        <div className='flex-1 min-h-0'>
          <CampaignTable
            data={formatData(rows)}
            loadMore={loadMore}
            hasMore={page < totalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminCampaign;
